import React from "react"
import {
  Share2, Search, Megaphone, Lightbulb, Video, Link2, ListChecks, Swords,
  Calendar, AlertTriangle, ImageIcon, Tag, CheckCircle2, Eye, Users, Globe, Lock,
} from "lucide-react"
import { ReportCard, ReportSectionHeader, ReportStatTile, ReportEmptyState, ReportBadge } from "../components/ReportUI"
import { REPORT_COLORS } from "../components/reportTheme"
import { toArray, formatNumbersInText, fit } from "../components/reportUtils"
import { isBlurredField, blurAware, badgeAware, safeLower, BlurredInline, BlurredBlock, BlurredContentFade, BlurredGridPreview, BlurredStaticLines, partitionBlurred, PLACEHOLDER_WORD } from "../components/BlurredField"
import { buildAdLibraryUrl, buildAdLibrarySearchUrl } from "../../../utils/facebookAdsLibrary"

const VISIBLE_PLATFORM_COUNT = 3

const fmtFollowers = (n, size) => {
  if (isBlurredField(n)) return <BlurredInline field={n} size={size} />
  if (n == null) return "—"
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return `${n}`
}

// Free-text AI-generated list items sometimes come back as objects (e.g. a
// { theme|issue, examples } shape) rather than plain strings, or as the
// admin's blurred-field descriptor — read defensively. Prose may also embed
// raw unformatted numbers straight from the source data — clean those too.
const textOf = (item, size = "sm", interactive = true) => {
  if (isBlurredField(item)) return <BlurredInline field={item} size={size} interactive={interactive} />
  if (typeof item === "string") return formatNumbersInText(item)
  const raw = item?.suggestion ?? item?.text ?? item?.theme ?? item?.issue
  if (isBlurredField(raw)) return <BlurredInline field={raw} size={size} interactive={interactive} />
  return raw ? formatNumbersInText(raw) : JSON.stringify(item)
}

// True when this list entry itself (or its embedded text field) is the
// admin's blurred descriptor rather than real content.
const isBulletLocked = item => isBlurredField(item) || isBlurredField(item?.suggestion ?? item?.text ?? item?.theme ?? item?.issue)

// Keyword/tag badges (ad platforms, ad types, caption keywords...) — renders
// only the real tags. Locked ones are silently dropped HERE rather than each
// getting its own chip: the caller counts them (`toArray(items).filter(isBlurredField).length`)
// and folds that into the ONE combined Unlock for the whole card, since a
// card can use this list more than once (e.g. Ad Presence uses it for both
// platforms and ad types) and must still show only one button total.
const BadgeList = ({ items, tone = "neutral" }) =>
  toArray(items).filter(item => !isBlurredField(item)).map((item, i) => (
    <React.Fragment key={i}>{badgeAware(item, v => <ReportBadge tone={tone}>{textOf(v, "sm", false)}</ReportBadge>, undefined, false)}</React.Fragment>
  ))

const cap = value => (typeof value === "string" && value.length > 0 ? value[0].toUpperCase() + value.slice(1) : value)

const positionTone = position => {
  const p = safeLower(position)
  if (p.includes("leader")) return "good"
  if (p.includes("niche")) return "warning"
  return "neutral"
}

const threatTone = threat => {
  const t = safeLower(threat)
  if (t === "high") return "critical"
  if (t === "medium") return "warning"
  if (t === "low") return "good"
  return "neutral"
}

const priorityTone = priority => {
  const p = safeLower(priority)
  if (p === "high") return "critical"
  if (p === "medium") return "warning"
  if (p === "low") return "good"
  return "neutral"
}

export default function PublicSocialTab({ socialSection, seoSection, adsSection, marketingSection, platformSection, appName }) {
  const accountsBlurred = socialSection?.available && isBlurredField(socialSection.data) ? socialSection.data : null
  const seoBlurred = seoSection?.available && isBlurredField(seoSection.data) ? seoSection.data : null
  const adsBlurred = adsSection?.available && isBlurredField(adsSection.data) ? adsSection.data : null
  const marketingBlurred = marketingSection?.available && isBlurredField(marketingSection.data) ? marketingSection.data : null
  const platformIntelBlurred = platformSection?.available && isBlurredField(platformSection.data) ? platformSection.data : null

  const accounts = socialSection?.available && !accountsBlurred ? socialSection.data : null
  const seo = seoSection?.available && !seoBlurred ? seoSection.data : null
  const ads = adsSection?.available && !adsBlurred ? adsSection.data : null
  const marketing = marketingSection?.available && !marketingBlurred ? marketingSection.data : null
  const platformIntel = platformSection?.available && !platformIntelBlurred ? platformSection.data : null

  if (
    !accounts && !seo && !ads && !marketing && !platformIntel &&
    !accountsBlurred && !seoBlurred && !adsBlurred && !marketingBlurred && !platformIntelBlurred
  ) {
    return (
      <ReportEmptyState
        icon={Share2}
        title="Social & SEO not available"
        description="This part of the report hasn't been generated for this app yet."
      />
    )
  }

  const topAccounts = toArray(accounts?.accounts)
    .map(a => (isBlurredField(a) ? a : { ...a, followers: a.counts?.followers ?? a.counts?.subscribers ?? 0 }))
    .sort((a, b) => (isBlurredField(a) || isBlurredField(b) ? 0 : b.followers - a.followers))
    .slice(0, 4)

  const otherLinks = toArray(accounts?.other_links)
  const adEntries = toArray(ads?.ads?.ads)
  const activeAdCount = ads?.ads?.active_ad_count ?? ads?.active_ad_count
  const totalAdCount = ads?.ads?.ad_count ?? ads?.ad_count

  const socialIntel = marketing?.social
  const keyFindings = toArray(marketing?.key_findings)
  const platformPlays = toArray(socialIntel?.platforms)
  const accountFixes = toArray(socialIntel?.account_fixes)
  const threats = toArray(marketing?.competitors)
  const actions = toArray(marketing?.actions)
  const contentPlan = toArray(marketing?.content_plan)

  const optimizationSuggestions = toArray(seo?.optimization_suggestions)
  // The admin can lock an entire keyword list as ONE descriptor rather than
  // blurring each keyword individually — toArray() would otherwise collapse
  // that into a 1-item array and render as a single pill, making a list of
  // (say) 15 hidden keywords look like exactly one. Detect the whole-list
  // lock separately so it renders as its own "list is locked" placeholder.
  const keywordsUsedBlurred = isBlurredField(seo?.keywords_used) ? seo.keywords_used : null
  const keywordsUsed = keywordsUsedBlurred ? [] : toArray(seo?.keywords_used)
  const missingKeywordsBlurred = isBlurredField(seo?.missing_keywords) ? seo.missing_keywords : null
  const missingKeywords = missingKeywordsBlurred ? [] : toArray(seo?.missing_keywords)
  const recommendedKeywordsBlurred = isBlurredField(seo?.recommended_keywords) ? seo.recommended_keywords : null
  const recommendedKeywords = recommendedKeywordsBlurred ? [] : toArray(seo?.recommended_keywords)
  const contentGaps = toArray(seo?.content_gaps)
  const keywordFrequencyBlurred = isBlurredField(seo?.keyword_frequency) ? seo.keyword_frequency : null
  const keywordFrequency = seo?.keyword_frequency && !keywordFrequencyBlurred && typeof seo.keyword_frequency === "object"
    ? Object.entries(seo.keyword_frequency).filter(([, v]) => typeof v === "number").sort((a, b) => b[1] - a[1])
    : []

  const captionAdKeywords = toArray(socialIntel?.caption_and_ad_keywords)
  const socialVerdict = typeof socialIntel?.verdict === "string" || isBlurredField(socialIntel?.verdict) ? socialIntel.verdict : null
  const bestPlatform = socialIntel?.best_platform
  const weakestPlatform = socialIntel?.weakest_platform

  const adPlatforms = toArray(ads?.ads?.platforms)
  const adTypes = toArray(ads?.ads?.ad_types)
  const totalImpressions = ads?.ads?.total_impressions_est
  const adsVerified = ads?.ads?.verified === true

  const competitorAdsMap = ads?.competitor_ads && !isBlurredField(ads.competitor_ads) ? ads.competitor_ads : null
  const competitorAdEntries = competitorAdsMap ? Object.values(competitorAdsMap) : []
  const competitorsRunningAds = competitorAdEntries.filter(c => (c?.active_ad_count || c?.ad_count || 0) > 0).length

  // Platform intelligence: which of the app's own + competitors' apps are
  // present on which social platforms (Discord, X, LinkedIn, Twitch, etc. —
  // a broader set than social_accounts' scraped-follower-count platforms).
  // Every layer here can be independently admin-blurred — the whole counts
  // object, a single platform's count, the whole platforms map, or a single
  // app entry's is_target flag — so each layer is checked on its own rather
  // than silently dropping (or misreading as "absent") anything locked.
  const platformCountsBlurred = isBlurredField(platformIntel?.platform_counts) ? platformIntel.platform_counts : null
  const platformCountsRaw = platformIntel?.platform_counts && typeof platformIntel.platform_counts === "object" && !platformCountsBlurred
    ? platformIntel.platform_counts
    : {}
  // Keep entries whose count itself is locked (rather than dropping them,
  // which would make a platform with hidden data vanish from the list
  // entirely) — sort real numbers first, locked ones trail.
  const platformCounts = Object.entries(platformCountsRaw)
    .filter(([, v]) => typeof v === "number" || isBlurredField(v))
    .sort((a, b) => (typeof b[1] === "number" ? b[1] : -1) - (typeof a[1] === "number" ? a[1] : -1))
  const numericPlatformCounts = platformCounts.map(([, v]) => v).filter(v => typeof v === "number")
  const maxPlatformCount = numericPlatformCounts.length ? Math.max(...numericPlatformCounts) : 1

  const platformsBlurred = isBlurredField(platformIntel?.platforms) ? platformIntel.platforms : null
  const platformApps = platformIntel?.platforms && typeof platformIntel.platforms === "object" && !platformsBlurred
    ? platformIntel.platforms
    : {}
  // "present" only when a real (non-blurred) entry confirms it; "unknown"
  // when the platform's own app list is locked or contains locked entries
  // we can't rule the target out of — never silently reported as "absent"
  // just because the data happens to be gated.
  const targetStatusFor = platform => {
    if (platformsBlurred) return "unknown"
    const apps = toArray(platformApps[platform])
    if (apps.some(a => !isBlurredField(a) && a?.is_target === true)) return "present"
    return apps.some(isBlurredField) ? "unknown" : "absent"
  }
  const platformsPresentCount = platformCounts.filter(([p]) => targetStatusFor(p) === "present").length

  return (
    <div>
      <ReportSectionHeader
        icon={Share2}
        eyebrow="Social & SEO"
        title={appName ? `${appName} — Social Presence` : "Social Presence"}
        action={marketing?.position ? badgeAware(marketing.position, v => <ReportBadge tone={positionTone(v)}>{v}</ReportBadge>) : null}
      />

      {(() => {
        // Whole-section blur: a locked stat is omitted entirely instead of
        // shown as its own inline blurred chip — one shared CTA covers the
        // whole grid, same as Core Metrics on the AI CMO tab.
        const seoLocked = isBlurredField(seo?.seo_score)
        const adsLocked = isBlurredField(activeAdCount) || isBlurredField(totalAdCount)
        const marketingLocked = isBlurredField(marketing?.score)
        const scoreWhyLocked = isBlurredField(marketing?.score_why)
        const metricsLocked = seoLocked || adsLocked || marketingLocked || scoreWhyLocked
        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, ${fit(160)})`, gap: 12 }}>
              <ReportStatTile label="Total Followers" value={fmtFollowers(accounts?.summary?.total_followers, "stat")} sub={`${accounts?.summary?.accounts_count ?? 0} accounts`} />
              {!seoLocked && seo?.seo_score != null && (
                <ReportStatTile label="SEO Score" value={`${seo.seo_score}/${typeof seo.max_score === "number" ? seo.max_score : 10}`} />
              )}
              {!adsLocked && activeAdCount != null && (
                <ReportStatTile label="Active Ads" value={activeAdCount} sub={totalAdCount != null ? `${totalAdCount} total` : undefined} />
              )}
              {!marketingLocked && marketing?.score != null && <ReportStatTile label="Marketing Score" value={marketing.score} />}
            </div>
            {!scoreWhyLocked && marketing?.score_why && (
              <div style={{ fontSize: 11, color: REPORT_COLORS.textMuted, marginTop: 8 }}>{formatNumbersInText(marketing.score_why)}</div>
            )}
            {metricsLocked && (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <BlurredContentFade items={[]} wrap />
              </div>
            )}
          </div>
        )
      })()}

      {(accountsBlurred || seoBlurred) && (() => {
        // Both locked at once → one shared CTA below the pair instead of
        // two identical "Upgrade to Unlock" buttons side by side; only one
        // locked → that card keeps its own CTA, nothing to de-duplicate.
        const bothLocked = !!accountsBlurred && !!seoBlurred
        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, ${fit(320)})`, gap: 12 }}>
              {accountsBlurred && <BlurredBlock field={accountsBlurred} icon={Share2} title="Social accounts" hideCta={bothLocked} />}
              {seoBlurred && <BlurredBlock field={seoBlurred} icon={Search} title="SEO" hideCta={bothLocked} />}
            </div>
            {bothLocked && (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <BlurredContentFade items={[]} wrap />
              </div>
            )}
          </div>
        )
      })()}

      {(() => {
        // One shared card, one shared Unlock — Top Platforms and SEO
        // Suggestions can each independently have locked content.
        const { visible: visibleAccounts, locked: lockedAccounts } = partitionBlurred(topAccounts)
        const { visible: visibleLinks, locked: lockedLinks } = partitionBlurred(otherLinks)
        const hasAccountSubFieldLock = a => isBlurredField(a.platform) || isBlurredField(a.bio)
        const accountsLocked = lockedAccounts.length + lockedLinks.length + visibleAccounts.filter(hasAccountSubFieldLock).length

        const suggestionsList = optimizationSuggestions.slice(0, 3)
        const visibleSuggestions = suggestionsList.filter(item => !isBulletLocked(item))
        const suggestionsLocked = suggestionsList.length - visibleSuggestions.length

        const anyLocked = accountsLocked > 0 || suggestionsLocked > 0
        const hasBoth = !!accounts && !!seo

        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
              <div style={{ flex: "1 1 240px", minWidth: 0, borderRight: hasBoth ? `1px solid ${REPORT_COLORS.cardBorder}` : "none", paddingRight: hasBoth ? 20 : 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary, marginBottom: 12 }}>Top Platforms</div>
                {!accounts ? (
                  <div style={{ fontSize: 12, color: REPORT_COLORS.textMuted }}>Social accounts haven't been generated for this app yet.</div>
                ) : topAccounts.length === 0 && otherLinks.length === 0 ? (
                  <div style={{ fontSize: 12, color: REPORT_COLORS.textMuted }}>No connected social accounts found.</div>
                ) : visibleAccounts.length === 0 && visibleLinks.length === 0 ? (
                  <BlurredStaticLines lines={2} />
                ) : (
                  <>
                    {visibleAccounts.map((a, i) => (
                      <div key={i} style={{ padding: "8px 0", borderBottom: i < visibleAccounts.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 13, color: REPORT_COLORS.textSecondary, textTransform: "capitalize", display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                            {typeof a.url === "string" ? (
                              <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {!isBlurredField(a.platform) && a.platform}{a.handle && ` · @${a.handle}`}
                              </a>
                            ) : (
                              <>{!isBlurredField(a.platform) && a.platform}{a.handle && ` · @${a.handle}`}</>
                            )}
                            {a.verified && <CheckCircle2 size={11} color={REPORT_COLORS.good} />}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary, fontFamily: "monospace", flexShrink: 0 }}>{fmtFollowers(a.followers)}</span>
                        </div>
                        {a.bio && !isBlurredField(a.bio) && <div style={{ fontSize: 11, color: REPORT_COLORS.textMuted, marginTop: 2 }}>{a.bio}</div>}
                      </div>
                    ))}
                    {visibleLinks.length > 0 && (
                      <div style={{ marginTop: visibleAccounts.length > 0 ? 10 : 0, paddingTop: visibleAccounts.length > 0 ? 10 : 0, borderTop: visibleAccounts.length > 0 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none", display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {visibleLinks.map((link, i) => (
                          <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: REPORT_COLORS.accent, textDecoration: "none", textTransform: "capitalize" }}>
                            <Link2 size={11} /> {link.platform}
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <Search size={14} color={REPORT_COLORS.textMuted} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>SEO Suggestions</span>
                </div>
                {!seo ? (
                  <div style={{ fontSize: 12, color: REPORT_COLORS.textMuted }}>SEO hasn't been generated for this app yet.</div>
                ) : suggestionsList.length === 0 ? (
                  <div style={{ fontSize: 12, color: REPORT_COLORS.textMuted }}>No SEO suggestions available.</div>
                ) : visibleSuggestions.length === 0 ? (
                  <BlurredStaticLines lines={2} />
                ) : (
                  visibleSuggestions.map((item, i) => (
                    <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, display: "flex", gap: 8 }}>
                      <span style={{ color: REPORT_COLORS.accent, fontWeight: 700 }}>•</span>
                      {textOf(item, "lg", false)}
                    </div>
                  ))
                )}
              </div>
            </div>
            {anyLocked && (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <BlurredContentFade items={[]} />
              </div>
            )}
          </ReportCard>
        )
      })()}

      {/* SEO keywords: used / gaps / frequency */}
      {(keywordsUsed.length > 0 || missingKeywords.length > 0 || recommendedKeywords.length > 0 || keywordsUsedBlurred || missingKeywordsBlurred || recommendedKeywordsBlurred) && (
        <ReportCard style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Tag size={14} color={REPORT_COLORS.textMuted} />
            <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Keywords</span>
            {seo?.competitors_analyzed != null && badgeAware(seo.competitors_analyzed, v => <ReportBadge tone="neutral">vs. {v} competitors</ReportBadge>, undefined, false)}
          </div>
          {/* A whole locked list (keywordsUsedBlurred etc.) still gets its
              own heading + a couple of static blurred lines here — not a
              row of blurred placeholder pills, but not silently skipped
              either, so each gated list is visible as "gated" on its own,
              ahead of the ONE shared Unlock below. */}
          {(keywordsUsed.length > 0 || keywordsUsedBlurred) && (
            <div style={{ marginBottom: missingKeywords.length > 0 || missingKeywordsBlurred || recommendedKeywords.length > 0 || recommendedKeywordsBlurred ? 12 : 0 }}>
              <div
                style={{ fontSize: 11, color: REPORT_COLORS.textMuted, marginBottom: 6, ...(keywordsUsedBlurred ? { filter: "blur(4px)", opacity: 0.85, userSelect: "none" } : {}) }}
                aria-hidden={keywordsUsedBlurred || undefined}
              >
                Currently used
              </div>
              {keywordsUsedBlurred ? (
                <BlurredStaticLines lines={1} />
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  <BadgeList items={keywordsUsed} tone="good" />
                </div>
              )}
            </div>
          )}
          {(missingKeywords.length > 0 || missingKeywordsBlurred) && (
            <div style={{ marginBottom: recommendedKeywords.length > 0 || recommendedKeywordsBlurred ? 12 : 0 }}>
              {/* When this whole list is gated, its own heading is blurred
                  right along with the pills below it — a plain, legible
                  label sitting above a blurred list read as a mismatch
                  (as if the label itself weren't gated content). */}
              <div
                style={{ fontSize: 11, color: REPORT_COLORS.textMuted, marginBottom: 6, ...(missingKeywordsBlurred ? { filter: "blur(4px)", opacity: 0.85, userSelect: "none" } : {}) }}
                aria-hidden={missingKeywordsBlurred || undefined}
              >
                Missing (competitors use, you don't)
              </div>
              {missingKeywordsBlurred ? (
                <BlurredStaticLines lines={1} />
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  <BadgeList items={missingKeywords} tone="warning" />
                </div>
              )}
            </div>
          )}
          {(recommendedKeywords.length > 0 || recommendedKeywordsBlurred) && (
            <div>
              <div
                style={{ fontSize: 11, color: REPORT_COLORS.textMuted, marginBottom: 6, ...(recommendedKeywordsBlurred ? { filter: "blur(4px)", opacity: 0.85, userSelect: "none" } : {}) }}
                aria-hidden={recommendedKeywordsBlurred || undefined}
              >
                Recommended
              </div>
              {recommendedKeywordsBlurred ? (
                <BlurredStaticLines lines={1} />
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  <BadgeList items={recommendedKeywords} tone="neutral" />
                </div>
              )}
            </div>
          )}
          {(keywordsUsedBlurred || missingKeywordsBlurred || recommendedKeywordsBlurred || isBlurredField(seo?.competitors_analyzed)) && (
            <div style={{ marginTop: 12, textAlign: "center" }}>
              <BlurredContentFade items={[]} />
            </div>
          )}
        </ReportCard>
      )}

      {(keywordFrequencyBlurred || keywordFrequency.length > 0 || contentGaps.length > 0) && (() => {
        // One shared card, one shared Unlock — same treatment as every
        // other paired-column section in the report.
        const gapsList = toArray(contentGaps)
        const visibleGaps = gapsList.filter(item => !isBulletLocked(item))
        const gapsLocked = gapsList.length - visibleGaps.length
        const anyLocked = !!keywordFrequencyBlurred || gapsLocked > 0
        const hasBoth = (keywordFrequencyBlurred || keywordFrequency.length > 0) && contentGaps.length > 0

        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
              {(keywordFrequencyBlurred || keywordFrequency.length > 0) && (
                <div style={{ flex: "1 1 240px", minWidth: 0, borderRight: hasBoth ? `1px solid ${REPORT_COLORS.cardBorder}` : "none", paddingRight: hasBoth ? 20 : 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary, marginBottom: 12 }}>Keyword Frequency</div>
                  {keywordFrequencyBlurred ? (
                    <BlurredStaticLines lines={2} />
                  ) : (() => {
                    const max = keywordFrequency[0]?.[1] || 1
                    return keywordFrequency.slice(0, 10).map(([kw, count], i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, width: 110, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{kw}</span>
                        <div style={{ flex: 1, height: 6, borderRadius: 4, backgroundColor: REPORT_COLORS.cardBgRaised, overflow: "hidden" }}>
                          <div style={{ width: `${(count / max) * 100}%`, height: "100%", backgroundColor: REPORT_COLORS.accent }} />
                        </div>
                        <span style={{ fontSize: 11, color: REPORT_COLORS.textMuted, width: 24, textAlign: "right", flexShrink: 0 }}>{count}</span>
                      </div>
                    ))
                  })()}
                </div>
              )}

              {contentGaps.length > 0 && (
                <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary, marginBottom: 12 }}>Content Gaps</div>
                  {visibleGaps.length === 0 ? (
                    <BlurredStaticLines lines={2} />
                  ) : (
                    visibleGaps.map((item, i) => (
                      <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, display: "flex", gap: 8 }}>
                        <span style={{ color: REPORT_COLORS.accent, fontWeight: 700 }}>•</span>
                        {textOf(item, "lg", false)}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {anyLocked && (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <BlurredContentFade items={[]} />
              </div>
            )}
          </ReportCard>
        )
      })()}

      {/* Ad creative */}
      {adsBlurred && (
        <div style={{ marginBottom: 16 }}>
          <BlurredBlock field={adsBlurred} icon={Video} title="Ad intelligence" />
        </div>
      )}
      {!ads && !adsBlurred && (
        <ReportCard style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Video size={14} color={REPORT_COLORS.textMuted} />
            <span style={{ fontSize: 12, color: REPORT_COLORS.textMuted }}>Ad intelligence hasn't been generated for this app yet.</span>
          </div>
        </ReportCard>
      )}
      {(adPlatforms.length > 0 || adTypes.length > 0 || totalImpressions != null || adsVerified || competitorAdsMap) && (() => {
        // One card, several independently-lockable pieces (impressions,
        // platform tags, ad-type tags) — combined into ONE total instead of
        // each piece showing its own Unlock.
        const totalLocked =
          (isBlurredField(totalImpressions) ? 1 : 0) +
          adPlatforms.filter(isBlurredField).length +
          adTypes.filter(isBlurredField).length
        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <Video size={14} color={REPORT_COLORS.textMuted} />
              <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Ad Presence</span>
              {adsVerified && <ReportBadge tone="good"><CheckCircle2 size={11} style={{ marginRight: 3, verticalAlign: -1 }} />Verified advertiser</ReportBadge>}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: (adPlatforms.length > 0 || adTypes.length > 0) ? 12 : 0 }}>
              {totalImpressions != null && (
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Eye size={12} /> {blurAware(totalImpressions, v => `${v.toLocaleString()} est. impressions`, undefined, false)}
                </span>
              )}
              {competitorAdsMap && (
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Users size={12} /> {competitorsRunningAds} of {competitorAdEntries.length} competitors running ads
                </span>
              )}
            </div>
            {adPlatforms.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: adTypes.length > 0 ? 8 : 0 }}>
                <BadgeList items={adPlatforms} />
              </div>
            )}
            {adTypes.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                <BadgeList items={adTypes} />
              </div>
            )}
            {totalLocked > 0 && <BlurredContentFade items={[]} />}
            {/* Meta confirms ad activity (ad_count > 0) but the backend only
                populates full per-ad creative details for currently-active
                ads — when active_ad_count is 0, adEntries below ends up
                empty even though ads exist. Rather than show a count with
                nothing to click, fall back to a real Ads Library search for
                this page/app name. */}
            {adEntries.length === 0 && totalAdCount > 0 && (
              <a
                href={buildAdLibrarySearchUrl(ads?.app_name || appName)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: REPORT_COLORS.accent, textDecoration: "none", marginTop: 12 }}
              >
                <Video size={12} /> Search {totalAdCount} ad{totalAdCount === 1 ? "" : "s"} on Facebook Ads Library
              </a>
            )}
          </ReportCard>
        )
      })()}
      {adEntries.length > 0 && (() => {
        // An ad whose headline is locked has nothing meaningful left to
        // show as a card — dropped from the visible grid entirely, same
        // "no orphan content" fix as Blocking Issues elsewhere. cta is
        // secondary — a visible ad can still have that locked individually,
        // but rather than an inline lock badge on every affected card, the
        // field is just omitted from that card; both cases fold into the
        // same locked count, so the grid preview and the one shared Unlock
        // below both agree on how many are gated.
        const { visible: rawVisible, locked } = partitionBlurred(adEntries.slice(0, 4))
        const visible = rawVisible.filter(ad => !isBlurredField(ad.headline))
        const totalLocked = locked.length + (rawVisible.length - visible.length) +
          visible.filter(ad => isBlurredField(ad.cta)).length
        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <ImageIcon size={14} color={REPORT_COLORS.textMuted} />
              <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Ad Creative</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, ${fit(200)})`, gap: 12 }}>
              {visible.map((ad, i) => (
                <a
                  key={ad._id || i}
                  href={buildAdLibraryUrl(ad)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "block", borderRadius: 10, backgroundColor: REPORT_COLORS.cardBgRaised, overflow: "hidden", textDecoration: "none" }}
                >
                  {ad.image_urls?.[0] ? (
                    <img src={ad.image_urls[0]} alt={ad.headline || "Ad creative"} style={{ width: "100%", height: 110, objectFit: "cover" }} />
                  ) : (
                    // No creative image on this ad — a real placeholder tile
                    // (not just skipping the image) so the card keeps the
                    // same height/shape as its siblings instead of looking
                    // cut off next to ones that do have an image.
                    <div style={{ width: "100%", height: 110, backgroundColor: REPORT_COLORS.cardBorder, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <ImageIcon size={28} color={REPORT_COLORS.textMuted} />
                    </div>
                  )}
                  <div style={{ padding: 10 }}>
                    <div style={{ fontSize: 12, color: REPORT_COLORS.textPrimary, fontWeight: 600, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {ad.headline || ad.title}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11, color: REPORT_COLORS.textMuted }}>
                      {!isBlurredField(ad.cta) && <span>{ad.cta || ad.cta_text}</span>}
                      {!ad.is_active && <ReportBadge tone="neutral">Inactive</ReportBadge>}
                    </div>
                  </div>
                </a>
              ))}
              {totalLocked > 0 && (
                <BlurredGridPreview
                  count={Math.min(totalLocked, 3)}
                  height={168}
                  renderPlaceholder={() => (
                    <div style={{ borderRadius: 10, backgroundColor: REPORT_COLORS.cardBgRaised, overflow: "hidden", height: "100%", boxSizing: "border-box" }}>
                      <div style={{ width: "100%", height: 110, backgroundColor: REPORT_COLORS.cardBorder }} />
                      <div style={{ padding: 10 }}>
                        <div style={{ fontSize: 12, color: REPORT_COLORS.textPrimary, fontWeight: 600 }}>{PLACEHOLDER_WORD}</div>
                      </div>
                    </div>
                  )}
                />
              )}
            </div>
            {totalLocked > 0 && (
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <BlurredContentFade items={[]} />
              </div>
            )}
          </ReportCard>
        )
      })()}

      {/* Marketing intelligence */}
      {marketingBlurred && (
        <div style={{ marginBottom: 16 }}>
          <BlurredBlock field={marketingBlurred} icon={Megaphone} title="Marketing intelligence" />
        </div>
      )}
      {marketing?.summary && (() => {
        // An action whose own text ("do") is locked has nothing to show
        // next to its priority badge — treated as locked, same fix as
        // Blocking Issues on the AI CMO tab.
        const { visible: rawVisible, locked } = partitionBlurred(actions)
        const visible = rawVisible.filter(action => !isBlurredField(action.do))
        const totalLocked = (isBlurredField(marketing.summary) ? 1 : 0) + locked.length + (rawVisible.length - visible.length)
        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Megaphone size={14} color={REPORT_COLORS.accent} />
              <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Marketing Intelligence</span>
            </div>
            <div style={{ marginBottom: 12 }}>
              {isBlurredField(marketing.summary) ? (
                <BlurredStaticLines lines={2} />
              ) : (
                <div style={{ fontSize: 13, color: REPORT_COLORS.textSecondary }}>{formatNumbersInText(marketing.summary)}</div>
              )}
            </div>

            {actions.length > 0 && (
              <div style={{ paddingTop: 10, borderTop: `1px solid ${REPORT_COLORS.cardBorder}` }}>
                {visible.length === 0 ? (
                  <BlurredStaticLines lines={2} />
                ) : (
                  visible.map((action, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: i < visible.length - 1 ? 10 : 0 }}>
                      <Lightbulb size={14} color={REPORT_COLORS.warning} style={{ marginTop: 1, flexShrink: 0 }} />
                      <div style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, flex: 1, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span>{formatNumbersInText(action.do)}</span>
                        {action.priority && !isBlurredField(action.priority) && <ReportBadge tone={priorityTone(action.priority)}>{action.priority}</ReportBadge>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            {totalLocked > 0 && <BlurredContentFade items={[]} />}
          </ReportCard>
        )
      })()}

      {keyFindings.length > 0 && (
        <ReportCard style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <ListChecks size={14} color={REPORT_COLORS.textMuted} />
            <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Key Findings</span>
          </div>
          {(() => {
            const { visible, locked } = partitionBlurred(keyFindings)
            const hasSubFieldLock = f => isBlurredField(f?.suggestion ?? f?.text ?? f?.theme ?? f?.issue)
            const totalLocked = locked.length + visible.filter(hasSubFieldLock).length
            return (
              <>
                {visible.length === 0 ? (
                  <BlurredStaticLines lines={2} />
                ) : (
                  visible.map((finding, i) => (
                    <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, paddingBottom: 8, borderBottom: i < visible.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                      {textOf(finding, "lg", false)}
                    </div>
                  ))
                )}
                {totalLocked > 0 && <BlurredContentFade items={[]} />}
              </>
            )
          })()}
        </ReportCard>
      )}

      {(socialVerdict || bestPlatform || weakestPlatform || captionAdKeywords.length > 0) && (() => {
        const totalLocked =
          (isBlurredField(bestPlatform) ? 1 : 0) +
          (isBlurredField(weakestPlatform) ? 1 : 0) +
          (isBlurredField(socialVerdict) ? 1 : 0) +
          captionAdKeywords.filter(isBlurredField).length
        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: socialVerdict ? 10 : 0, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Social Verdict</span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {bestPlatform && (isBlurredField(bestPlatform) ? <BlurredInline field={bestPlatform} interactive={false} /> : <ReportBadge tone="good">Best: {cap(bestPlatform)}</ReportBadge>)}
                {weakestPlatform && (isBlurredField(weakestPlatform) ? <BlurredInline field={weakestPlatform} interactive={false} /> : <ReportBadge tone="warning">Weakest: {cap(weakestPlatform)}</ReportBadge>)}
              </div>
            </div>
            {socialVerdict && <div style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: captionAdKeywords.length > 0 ? 12 : 0 }}>{blurAware(socialVerdict, formatNumbersInText, "lg", false)}</div>}
            {captionAdKeywords.length > 0 && (
              <div>
                <div style={{ fontSize: 11, color: REPORT_COLORS.textMuted, marginBottom: 6 }}>Caption &amp; ad keywords competitors lean on</div>
                {captionAdKeywords.every(isBlurredField) ? (
                  <BlurredStaticLines lines={1} />
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    <BadgeList items={captionAdKeywords} />
                  </div>
                )}
              </div>
            )}
            {totalLocked > 0 && <BlurredContentFade items={[]} />}
          </ReportCard>
        )
      })()}

      {(platformPlays.length > 0 || accountFixes.length > 0) && (() => {
        // A play whose platform name is locked has no heading to show next
        // to its priority badge — same "no orphan badge" fix as elsewhere.
        const { visible: rawVisiblePlays, locked: lockedPlays } = partitionBlurred(platformPlays)
        const visiblePlays = rawVisiblePlays.filter(p => !isBlurredField(p.platform))
        const playsLocked = lockedPlays.length + (rawVisiblePlays.length - visiblePlays.length)

        const visibleFixes = toArray(accountFixes).filter(item => !isBulletLocked(item))
        const fixesLocked = accountFixes.length - visibleFixes.length

        const anyLocked = playsLocked > 0 || fixesLocked > 0
        const hasBoth = platformPlays.length > 0 && accountFixes.length > 0

        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
              {platformPlays.length > 0 && (
                <div style={{ flex: "1 1 240px", minWidth: 0, borderRight: hasBoth ? `1px solid ${REPORT_COLORS.cardBorder}` : "none", paddingRight: hasBoth ? 20 : 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary, marginBottom: 12 }}>Platform Playbook</div>
                  {visiblePlays.length === 0 ? (
                    <BlurredStaticLines lines={2} />
                  ) : (
                    visiblePlays.map((p, i) => (
                      <div key={i} style={{ marginBottom: i < visiblePlays.length - 1 ? 10 : 0, paddingBottom: i < visiblePlays.length - 1 ? 10 : 0, borderBottom: i < visiblePlays.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: REPORT_COLORS.textPrimary, textTransform: "capitalize" }}>{p.platform}</span>
                          {p.priority && !isBlurredField(p.priority) && <ReportBadge tone={priorityTone(p.priority)}>{p.priority}</ReportBadge>}
                        </div>
                        {p.read && !isBlurredField(p.read) && <div style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 2 }}>{formatNumbersInText(p.read)}</div>}
                        {p.action && !isBlurredField(p.action) && <div style={{ fontSize: 12, color: REPORT_COLORS.textMuted, fontStyle: "italic" }}>→ {formatNumbersInText(p.action)}</div>}
                      </div>
                    ))
                  )}
                </div>
              )}

              {accountFixes.length > 0 && (
                <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <AlertTriangle size={14} color={REPORT_COLORS.warning} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Account Fixes</span>
                  </div>
                  {visibleFixes.length === 0 ? (
                    <BlurredStaticLines lines={2} />
                  ) : (
                    visibleFixes.map((item, i) => (
                      <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, display: "flex", gap: 8 }}>
                        <span style={{ color: REPORT_COLORS.warning, fontWeight: 700 }}>•</span>
                        {textOf(item, "lg", false)}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {anyLocked && (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <BlurredContentFade items={[]} />
              </div>
            )}
          </ReportCard>
        )
      })()}

      {threats.length > 0 && (() => {
        // A threat whose name is locked has nothing meaningful left to show
        // as a card — dropped from the visible grid entirely, same "no
        // orphan content" fix as Blocking Issues elsewhere. threat/why/
        // exploit are secondary — a visible threat can still have those
        // locked individually, but rather than an inline lock badge on
        // every affected card, the field is just omitted from that card;
        // both cases fold into the same locked count, so the grid preview
        // and the one shared Unlock below both agree on how many are gated.
        const { visible: rawVisible, locked } = partitionBlurred(threats)
        const visible = rawVisible.filter(t => !isBlurredField(t.name))
        const totalLocked = locked.length + (rawVisible.length - visible.length) +
          visible.filter(t => isBlurredField(t.threat) || isBlurredField(t.why) || isBlurredField(t.exploit)).length
        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Swords size={14} color={REPORT_COLORS.critical} />
              <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Competitor Threats</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, ${fit(260)})`, gap: 10 }}>
              {visible.map((t, i) => (
                <div key={i} style={{ padding: 10, borderRadius: 8, backgroundColor: REPORT_COLORS.cardBgRaised }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>{t.name}</span>
                    {t.threat && !isBlurredField(t.threat) && (
                      <ReportBadge tone={threatTone(t.threat)}>{t.threat}</ReportBadge>
                    )}
                  </div>
                  {t.why && !isBlurredField(t.why) && <div style={{ fontSize: 11, color: REPORT_COLORS.textSecondary, marginBottom: 2 }}>{formatNumbersInText(t.why)}</div>}
                  {t.exploit && !isBlurredField(t.exploit) && <div style={{ fontSize: 11, color: REPORT_COLORS.textMuted, fontStyle: "italic" }}>→ {formatNumbersInText(t.exploit)}</div>}
                </div>
              ))}
              {totalLocked > 0 && (
                <BlurredGridPreview
                  count={Math.min(totalLocked, 3)}
                  height={88}
                  renderPlaceholder={() => (
                    <div style={{ padding: 10, borderRadius: 8, backgroundColor: REPORT_COLORS.cardBgRaised, height: "100%", boxSizing: "border-box" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: REPORT_COLORS.textPrimary, marginBottom: 6 }}>{PLACEHOLDER_WORD}</div>
                      <div style={{ fontSize: 11, color: REPORT_COLORS.textSecondary }}>{PLACEHOLDER_WORD}</div>
                    </div>
                  )}
                />
              )}
            </div>
            {totalLocked > 0 && (
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <BlurredContentFade items={[]} />
              </div>
            )}
          </ReportCard>
        )
      })()}

      {/* Platform intelligence: who's on which platform, and who isn't */}
      {platformIntelBlurred && (
        <div style={{ marginBottom: 16 }}>
          <BlurredBlock field={platformIntelBlurred} icon={Globe} title="Platform intelligence" />
        </div>
      )}
      {platformCountsBlurred && (
        <div style={{ marginBottom: 16 }}>
          <BlurredBlock field={platformCountsBlurred} icon={Globe} title="Platform coverage" />
        </div>
      )}
      {(platformCounts.length > 0 || contentPlan.length > 0) && (
        <ReportCard>
          {(() => {
            // `decorative` renders inside BlurredContentFade's preview stack,
            // which already owns ONE "Unlock" affordance for the whole faded
            // region — a row there shouldn't also compute/show its own real
            // status icon, per-count Unlock chip, or gap-competitor list, or
            // it reads as locked twice. It just shows the (real, harmless)
            // platform name plus plausible placeholder count/bar, and lets
            // the fade's blur do the "obscured" work.
            const renderPlatformRow = (entry, i, decorative = false) => {
              const [platform, count] = entry
              if (decorative) {
                return (
                  <div key={`p-${i}`} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, textTransform: "capitalize", display: "flex", alignItems: "center", gap: 6 }}>
                        <Globe size={12} color={REPORT_COLORS.textMuted} />
                        {platform}
                      </span>
                      <span style={{ fontSize: 11, color: REPORT_COLORS.textMuted, flexShrink: 0 }}>— apps</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 4, backgroundColor: REPORT_COLORS.cardBgRaised, overflow: "hidden" }}>
                      <div style={{ width: "45%", height: "100%", backgroundColor: REPORT_COLORS.textMuted }} />
                    </div>
                  </div>
                )
              }

              const countBlurred = isBlurredField(count)
              const status = targetStatusFor(platform)
              const present = status === "present"
              const unknown = status === "unknown"
              const gapCompetitors = status === "absent"
                ? toArray(platformApps[platform]).filter(a => a?.is_target !== true).slice(0, 6)
                : []
              return (
                <div key={`p-${platform}`} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, textTransform: "capitalize", display: "flex", alignItems: "center", gap: 6 }}>
                      {present ? (
                        <CheckCircle2 size={12} color={REPORT_COLORS.good} />
                      ) : unknown ? (
                        <Lock size={12} color={REPORT_COLORS.accent} />
                      ) : (
                        <AlertTriangle size={12} color={REPORT_COLORS.textMuted} />
                      )}
                      {platform}
                    </span>
                    {/* A locked count is just omitted (no inline lock badge) —
                        this row already folds into visibleRowLocked/
                        showUnlockPanel below, same "no per-item badge" fix
                        as the gap-competitor list underneath. */}
                    {!countBlurred && <span style={{ fontSize: 11, color: REPORT_COLORS.textMuted, flexShrink: 0 }}>{count} apps</span>}
                  </div>
                  {!countBlurred && (
                    <div style={{ height: 6, borderRadius: 4, backgroundColor: REPORT_COLORS.cardBgRaised, overflow: "hidden" }}>
                      <div style={{ width: `${(count / maxPlatformCount) * 100}%`, height: "100%", backgroundColor: present ? REPORT_COLORS.good : REPORT_COLORS.accent }} />
                    </div>
                  )}
                  {(() => {
                    const visibleGaps = gapCompetitors.filter(a => !isBlurredField(a) && !isBlurredField(a.name))
                    return visibleGaps.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                        {visibleGaps.map((a, i2) =>
                          typeof a.url === "string" ? (
                            <a key={i2} href={a.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: REPORT_COLORS.accent, textDecoration: "none" }}>
                              {a.name}
                            </a>
                          ) : (
                            <span key={i2} style={{ fontSize: 11, color: REPORT_COLORS.textMuted }}>{a.name}</span>
                          )
                        )}
                      </div>
                    )
                  })()}
                </div>
              )
            }

            const visiblePlatforms = platformCounts.slice(0, VISIBLE_PLATFORM_COUNT)
            const remainingPlatforms = platformCounts.slice(VISIBLE_PLATFORM_COUNT)
            const remainingPlatformsLocked = remainingPlatforms.some(([p, count]) => isBlurredField(count) || targetStatusFor(p) === "unknown")
            // A visible platform row's own count, or one of its gap-competitor
            // entries, can be locked even though the row itself is shown —
            // that needs a way to unlock too, folded into the same one panel
            // below rather than a button of its own on the row.
            const visibleRowLocked = visiblePlatforms.some(([platform, count]) => {
              if (isBlurredField(count)) return true
              if (targetStatusFor(platform) !== "absent") return false
              return toArray(platformApps[platform]).filter(a => a?.is_target !== true).slice(0, 6).some(isBlurredField)
            }) || contentPlan.some(plan =>
              isBlurredField(plan?.week) || isBlurredField(plan?.platform) || isBlurredField(plan?.post) || isBlurredField(plan?.goal)
            )

            // Content Plan no longer gets its own partial preview — either
            // every week is real (fully entitled) and it shows in full, or
            // any of it is locked and the whole section disappears rather
            // than teasing a couple of weeks. What's hidden is instead named
            // in the single shared Unlock panel below.
            const contentPlanLocked = contentPlan.some(isBlurredField)
            const showUnlockPanel = remainingPlatformsLocked || contentPlanLocked || visibleRowLocked

            return (
              <>
                {platformCounts.length > 0 && (
                  <div style={{ marginBottom: !contentPlanLocked && contentPlan.length > 0 ? 20 : 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Globe size={14} color={REPORT_COLORS.textMuted} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Platform Coverage</span>
                      </div>
                      <ReportBadge tone={platformsPresentCount === platformCounts.length ? "good" : "warning"}>
                        On {platformsPresentCount}/{platformCounts.length} platforms
                      </ReportBadge>
                    </div>
                    {visiblePlatforms.map((entry, i) => renderPlatformRow(entry, i))}
                    {!remainingPlatformsLocked && remainingPlatforms.map((entry, i) => renderPlatformRow(entry, i))}
                  </div>
                )}

                {!contentPlanLocked && contentPlan.length > 0 && (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                      <Calendar size={14} color={REPORT_COLORS.textMuted} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Content Plan</span>
                    </div>
                    {/* Rows here are only reached when contentPlanLocked is
                        false (no whole entry is locked). A sub-field on one
                        can still be locked — same "no inline lock badge" fix
                        as Platform Playbook: the field is just omitted from
                        the row rather than shown as its own blurred chip,
                        and folds into showUnlockPanel above instead. */}
                    {contentPlan.map((plan, i) => (
                      <div key={i} style={{ display: "flex", gap: 10, marginBottom: i < contentPlan.length - 1 ? 10 : 0, paddingBottom: i < contentPlan.length - 1 ? 10 : 0, borderBottom: i < contentPlan.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                        {plan.week != null && !isBlurredField(plan.week) && <ReportBadge tone="neutral">{plan.week}</ReportBadge>}
                        <div style={{ flex: 1 }}>
                          {!isBlurredField(plan.platform) && <div style={{ fontSize: 12, color: REPORT_COLORS.textPrimary, fontWeight: 600, textTransform: "capitalize" }}>{plan.platform}</div>}
                          {plan.post && !isBlurredField(plan.post) && <div style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginTop: 2 }}>{formatNumbersInText(plan.post)}</div>}
                          {plan.goal && !isBlurredField(plan.goal) && <div style={{ fontSize: 11, color: REPORT_COLORS.textMuted, fontStyle: "italic", marginTop: 2 }}>Goal: {formatNumbersInText(plan.goal)}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {showUnlockPanel && (
                  <BlurredContentFade
                    items={remainingPlatformsLocked ? remainingPlatforms : []}
                    renderItem={(entry, i) => renderPlatformRow(entry, i, true)}
                    subtitle="To see the platform coverage, content plan and many more, please upgrade to plan."
                  />
                )}
              </>
            )
          })()}
        </ReportCard>
      )}
    </div>
  )
}
